if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        if (this.hasAttribute('data-quadri-media')) {
          this.init();
          return;
        }
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      init() {
        if (this.hasAttribute('data-quadri-media')) {
          this.initQuadriGallery();
          return;
        }
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (this.hasAttribute('data-quadri-media')) {
          this.updateQuadriMediaLayout();
          return;
        }
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        // Store the current index before closing
        let lastLightboxIndex = null;
        this.lightbox.on('close', () => {
          if (this.lightbox.pswp) {
            lastLightboxIndex = this.lightbox.pswp.currIndex;
          }
        });

        // Update slider after lightbox is destroyed
        this.lightbox.on('destroy', () => {
          if (this.quadriRebuilding) return;
          if (this.sliderInstance && this.sliderInstance.slider && lastLightboxIndex !== null) {
            const slider = this.sliderInstance.slider;
            const targetIndex = lastLightboxIndex;
            // currIndex from lightbox is the index in dataSource array
            // which should match the slider index since both are created from the same media items in the same order
            if (targetIndex >= 0 && targetIndex < slider.slides.length && slider.activeIndex !== targetIndex) {
              // Use requestAnimationFrame to ensure DOM is ready
              requestAnimationFrame(() => {
                if (!slider.destroyed && !this.quadriRebuilding) slider.slideTo(targetIndex, 0);
              });
            }
            lastLightboxIndex = null;
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        const removeZoomListener = FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
        if (this.hasAttribute('data-quadri-media')) this.quadriZoomUnsubscribe = removeZoomListener;
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (this.hasAttribute('data-quadri-media')) {
          const material = variant?.options?.[Number(this.dataset.quadriMaterialIndex)] ?? this.dataset.quadriSelectedMaterial;
          this.filterQuadriMaterial(material, variant?.featured_media?.id ?? variant?.featured_image?.id);
          return;
        }
        if (!variant) return;

        let featuredMedia = variant.featured_media;

        if (!featuredMedia && variant.featured_image) {
          featuredMedia = {
            id: variant.featured_image.id,
            position: variant.featured_image.position || 0,
            media_type: 'image',
          };
        }

        if (!featuredMedia) return;

        if (this.sliderInstance.slider) {
          const slideIndex = featuredMedia.position || 0;
          this.sliderInstance.slider.slideTo(slideIndex - 1, 0, false);
        } else {
          this.sortMediaItems(variant, featuredMedia);
        }
      }

      initQuadriGallery() {
        if (this.quadriGalleryReady) return;
        this.elements = FoxTheme.utils.queryDomNodes(this.selectors, this);
        if (!this.elements.mediaList || !this.elements.viewer) return;
        this.mediaLayout = this.dataset.mediaLayout;
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        // Keep the original nodes so changing back restores every image, without
        // keeping excluded images inside Swiper or PhotoSwipe's active slide lists.
        this.quadriAllMedia ||= Array.from(this.elements.mediaList.children);
        this.quadriThumbList = this.querySelector('[id^="Slider-Thumbnails"]');
        this.quadriAllThumbs ||= Array.from(this.quadriThumbList?.children || []);
        this.quadriGalleryReady = true;
        this.quadriMql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        this.quadriResize = () => this.updateQuadriMediaLayout();
        this.quadriMql.addEventListener('change', this.quadriResize);
        this.filterQuadriMaterial(this.dataset.quadriSelectedMaterial || '', null, true);
      }

      disconnectedCallback() {
        if (!this.hasAttribute('data-quadri-media')) return;
        this.quadriMql?.removeEventListener('change', this.quadriResize);
        this.quadriRebuilding = true;
        this.destroyQuadriSliders();
        this.destroyQuadriZoom();
        this.quadriGalleryReady = false;
        this.quadriRebuilding = false;
      }

      destroyQuadriSliders() {
        if (this.sliderInstance?.slider && !this.sliderInstance.slider.destroyed) {
          this.sliderInstance.slider.destroy(true, true);
        }
        if (this.thumbsInstance?.slider && !this.thumbsInstance.slider.destroyed) {
          this.thumbsInstance.slider.destroy(true, true);
        }
        this.sliderInstance = false;
        this.thumbsInstance = false;
        if (this.sliderOptions) delete this.sliderOptions.thumbs;
      }

      destroyQuadriZoom() {
        this.quadriZoomUnsubscribe?.();
        this.quadriZoomUnsubscribe = null;
        this.lightbox?.destroy();
        this.lightbox = null;
      }

      filterQuadriMaterial(material, featuredId = null, force = false) {
        if (!this.hasAttribute('data-quadri-media')) return;
        const selected = String(material || '').trim().toLowerCase();
        this.dataset.quadriSelectedMaterial = selected;
        if (!this.quadriGalleryReady) this.initQuadriGallery();
        if (!this.quadriGalleryReady) return;
        if (!force && this.quadriMaterial === selected) {
          if (featuredId != null) this.activateQuadriMedia(String(featuredId));
          return;
        }

        this.quadriRebuilding = true;
        window.pauseAllMedia?.(this);
        this.destroyQuadriSliders();
        this.destroyQuadriZoom();
        this.quadriMaterial = selected;
        const visible = this.quadriAllMedia.filter((media) => !media.dataset.quadriMaterial || media.dataset.quadriMaterial === selected);
        const ids = new Set(visible.map((media) => media.dataset.mediaId));
        const thumbs = this.quadriAllThumbs.filter((thumb) => ids.has(thumb.dataset.target));
        for (const media of [...visible, ...thumbs]) media.removeAttribute('data-quadri-media-hidden');
        this.elements.mediaList.replaceChildren(...visible);
        this.quadriThumbList?.replaceChildren(...thumbs);
        this.elements.mediaItems = visible;
        this.onlyImage = visible.length < 2;
        this.dataset.onlyImage = String(this.onlyImage);
        this.dataset.quadriMediaCount = String(visible.length);

        const preferred = visible.find((media) => media.dataset.mediaId === String(featuredId))
          || visible.find((media) => media.dataset.quadriMaterial === selected && selected)
          || visible[0];
        this.quadriActiveId = preferred?.dataset.mediaId;
        this.reindexQuadriMedia();
        const productInfo = this.closest('product-info');
        if (productInfo) {
          for (const name of Array.from(productInfo.classList)) {
            if (/^product--media-has-\d+$/.test(name)) productInfo.classList.remove(name);
          }
          productInfo.classList.add(`product--media-has-${visible.length}`);
          productInfo.classList.toggle('product--media-has-many', visible.length > 1);
          productInfo.classList.toggle('product--odd-media', this.mediaLayout === 'grid-mix' && visible.length % 3 === 2);
        }
        this.quadriRebuilding = false;
        this.updateQuadriMediaLayout();
      }

      reindexQuadriMedia() {
        this.elements.mediaItems = Array.from(this.elements.mediaList.children);
        this.elements.mediaItems.forEach((media, index) => {
          media.dataset.mediaIndex = String(index);
          media.querySelectorAll('[data-media-index]').forEach((button) => { button.dataset.mediaIndex = String(index); });
        });
        Array.from(this.quadriThumbList?.children || []).forEach((thumb, index) => { thumb.dataset.mediaPosition = String(index + 1); });
      }

      updateQuadriMediaLayout() {
        if (!this.quadriGalleryReady) return;
        const activeId = this.sliderInstance?.slider?.slides?.[this.sliderInstance.slider.activeIndex]?.dataset.mediaId || this.quadriActiveId;
        this.quadriRebuilding = true;
        this.destroyQuadriSliders();
        this.destroyQuadriZoom();
        this.setSliderOptions();
        if (this.elements.mediaItems.length > 1 && (this.quadriMql.matches || this.enableDesktopSlider)) this.initSlider();
        this.activateQuadriMedia(activeId, false);
        if (this.enableImageZoom && this.elements.mediaItems.length && FoxTheme.PhotoSwipeLightbox) this.initImageZoom();
        this.quadriRebuilding = false;
      }

      activateQuadriMedia(id, refreshZoom = true) {
        const selected = this.elements.mediaItems.find((media) => media.dataset.mediaId === String(id));
        // An image assigned to a variant can belong to another material: ignore it.
        if (!selected) return;
        this.quadriActiveId = selected.dataset.mediaId;
        if (this.sliderInstance?.slider) {
          const index = this.elements.mediaItems.indexOf(selected);
          this.sliderInstance.slider.slideTo(index, 0);
          this.thumbsInstance?.slider?.slideTo(index, 0);
        } else if (this.elements.mediaItems[0] !== selected) {
          this.elements.mediaList.prepend(selected);
          const thumb = this.quadriAllThumbs.find((item) => item.dataset.target === selected.dataset.mediaId);
          if (thumb && this.quadriThumbList?.contains(thumb)) this.quadriThumbList.prepend(thumb);
          this.reindexQuadriMedia();
          if (refreshZoom && this.enableImageZoom && FoxTheme.PhotoSwipeLightbox) {
            this.quadriRebuilding = true;
            this.destroyQuadriZoom();
            this.initImageZoom();
            this.quadriRebuilding = false;
          }
        }
      }


      sortMediaItems(variant, featuredMedia) {
        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (variant.featured_image
              ? {
                  id: variant.featured_image.id,
                  position: variant.featured_image.position || 0,
                  media_type: 'image',
                }
              : null);
        }

        if (!featuredMedia) return;

        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == featuredMedia.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${featuredMedia.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
